import React, { memo } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useLang } from '@nbx/frontend-helpers/subscribe';
import Copyright from './Copyright';
import Address from './Address';
import styles from './styles.module.scss';

const microapps = window.MICROFRONTENDS_CONFIG;

const FooterPortal = props => {
  return ReactDOM.createPortal(<Footer {...props} />, document.getElementById('footer_hook'));
};

const InternalLinkButton = ({ href, children, ...props }) => {
  return (
    <nbx-button-text muted href={window.PLATFORM === 'web' ? href : `https://nbx.com${href}`} {...props}>
      {children}
    </nbx-button-text>
  );
};

InternalLinkButton.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.any.isRequired
};

export function Footer(props) {
  const lang = useLang();
  const { t } = useTranslation();

  const isEnglish = lang === 'en';
  const langLink = `https://${window.location.host}/${isEnglish ? 'no' : 'en'}${
    props.user !== null ? microapps?.routes?.dashboards?.path : ''
  }`;
  const langLabel = isEnglish ? 'Norsk' : 'English';

  const setLang = newLang => {
    window.PUBLISH('lang', newLang);
  };

  let langButton;
  if (window.PLATFORM === 'web' || !window.PLATFORM) {
    langButton = (
      <a href={langLink} className={styles.langLink}>
        {langLabel}
      </a>
    );
  } else {
    langButton = (
      <nbx-button-text muted weight="regular" onClick={() => setLang(isEnglish ? 'no' : 'en')}>
        {langLabel}
      </nbx-button-text>
    );
  }

  // Footer is always dark theme
  return (
    <nbx-theme-provider theme="dark">
      <div className={classnames([styles.bar, { [styles.barGoogleAdsPage]: props.isGoogleAdsLandingPage }])}>
        <div
          className={classnames([
            styles.barContents,
            {
              [styles.barContentsTradingPage]: props.isTradingPage,
              [styles.barContentsLandingPage]: props.isLandingPage
            }
          ])}>
          <div className={styles.copyright}>
            <a href="https://www.nbx.com">
              <nbx-icon icon="nbx_logo" />
            </a>
            <Copyright />
            {props.isGoogleAdsLandingPage && <Address />}
            {!props.isMobile && langButton}
          </div>
          <div className={styles.columns}>
            <div className={styles.footerColumn}>
              <nbx-text styles={[styles.footerColumnHeader]}>{t('footer_title_company')}</nbx-text>
              <nbx-button-text muted href="https://www.nbx.com/en/about">
                {t('footer_link_about')}
              </nbx-button-text>
              <nbx-button-text muted href="https://www.nbx.com/en/careers" styles={[styles.footerLink]}>
                {t(`footer_link_careers`)}
              </nbx-button-text>
              <nbx-button-text muted href="https://www.nbx.com/en/privacy" styles={[styles.footerLink]}>
                {t('footer_link_privacy')}
              </nbx-button-text>
              <nbx-button-text muted href="https://www.nbx.com/en/security" styles={[styles.footerLink]}>
                {t('footer_link_security')}
              </nbx-button-text>
              <nbx-button-text
                muted
                href="https://www.nbx.com/en/terms-of-service"
                styles={[styles.footerLink]}>
                {t('footer_link_terms')}
              </nbx-button-text>
              <nbx-button-text muted href="https://www.nbx.com/en/cookies" styles={[styles.footerLink]}>
                {t('footer_link_cookies')}
              </nbx-button-text>
              {props.isMobile && langButton}
            </div>
            <div className={styles.footerColumn}>
              <nbx-text>{t('footer_title_system')}</nbx-text>
              <nbx-button-text href="https://nbxsupport.zendesk.com/hc/en-us" muted>
                {t('common_support')}
              </nbx-button-text>
              <InternalLinkButton href="/developers" styles={[styles.footerLink]}>
                {t('footer_link_apis')}
              </InternalLinkButton>
            </div>
            <div className={styles.footerColumn}>
              <nbx-text>{t('footer_title_social')}</nbx-text>
              <nbx-button-text muted href="https://www.nbx.com/en/blog">
                {t('footer_link_blog')}
              </nbx-button-text>
              <nbx-button-text muted href="https://twitter.com/nbxcom">
                {t('footer_link_twitter')}
              </nbx-button-text>
              <nbx-button-text muted href="https://www.facebook.com/nbxcom/">
                {t('footer_link_facebook')}
              </nbx-button-text>
              <nbx-button-text muted href="https://www.linkedin.com/company/norwegian-block-exchange/">
                {t('footer_link_linkedin')}
              </nbx-button-text>
              <nbx-button-text muted href="https://nbxsupport.zendesk.com/hc/en-us/requests/new">
                {t('footer_link_contact')}
              </nbx-button-text>
            </div>
          </div>
        </div>
      </div>
    </nbx-theme-provider>
  );
}

Footer.propTypes = {
  isLandingPage: PropTypes.bool.isRequired,
  isGoogleAdsLandingPage: PropTypes.bool.isRequired,
  isTradingPage: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  user: PropTypes.object
};

Footer.defaultProps = {
  user: undefined
};

export default memo(FooterPortal);
